<script lang="ts">
  import type {Writable} from 'svelte/store';
  import {writable} from 'svelte/store';
  import {setContext, getContext} from 'svelte';
  import {generatePalette, formatStyleString} from 'Catalyst/utility/theme';
  import type {CssColor, BrandColor, Background} from 'Catalyst/utility/theme';
  import type {ThemeContextProps} from '.';

  export let brandColor: ThemeContextProps['brandColor'] = undefined;
  export let brandColors: ThemeContextProps['brandColors'] = [];
  export let backgroundColor: ThemeContextProps['backgroundColor'] = undefined;
  export let overrideParentTheme: boolean = false;

  $: formattedBrandColors = brandColor
    ? [
        {
          key: 'brand-color',
          value: brandColor,
        },
      ]
    : brandColors;

  const brandColorsStore = writable<BrandColor[]>([]);
  const backgroundColorStore = writable<Background>();
  const theme = writable<Record<string, CssColor>>({});

  let palette: Record<string, CssColor> | undefined;

  const parentTheme = getContext<Writable<Record<string, CssColor>>>('theme');
  $: hasParentTheme = !!parentTheme;
  $: emptyParentTheme = parentTheme && Object.keys($parentTheme).length === 0;

  // if there is no parent theme in context, or the parent theme is empty, or we are overriding the parent theme
  // attempt to generate a new theme and set it in the theme store
  $: if (!hasParentTheme || emptyParentTheme || overrideParentTheme) {
    // if the brand colors are passed as props and the brand colors store is empty, set the brand colors store
    if (formattedBrandColors.length > 0 && $brandColorsStore?.length === 0) {
      backgroundColorStore.set(backgroundColor);
      brandColorsStore.set(formattedBrandColors);
    }

    // generate a new theme and set in the theme store
    palette =
      $brandColorsStore?.length > 0 &&
      generatePalette($brandColorsStore, $backgroundColorStore);
    theme.set(palette || {});
  }

  // if there is no parent theme in context or we are overriding the parent theme or the palette is generated and the parent theme is empty
  // set the context
  $: if (
    !hasParentTheme ||
    overrideParentTheme ||
    (palette && emptyParentTheme)
  ) {
    setContext('brandColors', brandColorsStore);
    setContext('backgroundColor', backgroundColorStore);
    setContext('theme', theme);
  }
</script>

{#if palette}
  <div style={formatStyleString(palette)}>
    <slot />
  </div>
{:else}
  <slot />
{/if}
